// Programs sidebar — folder tree with programs as leaf nodes.
// Supports: right-click rename (folders + programs) and drag-to-move
// (program → folder, folder → folder, or drop on "전체 서비스" to move to root).
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../shared/api/client";
import { I } from "../shared/Icon";
import { toast } from "../shared/stores/toasts";
import { useAuth } from "../shared/stores/auth";
import { ContextMenu, type CtxItem } from "./SubNav";
import { IconPicker, DEFAULT_ICON } from "../shared/components/IconPicker";

// ── Page type metadata ────────────────────────────────────────────────────────

export const PAGE_TYPES = [
  { id: "catalog",   label: "서비스 카탈로그", icon: "🗂️" },
  { id: "custom",    label: "커스텀 앱",        icon: "🧩" },
  { id: "embed",     label: "임베드 앱",        icon: "🖥️" },
  { id: "booking",   label: "예약 달력",       icon: "📅" },
  { id: "portfolio", label: "포트폴리오",       icon: "🖼️" },
  { id: "board",     label: "게시판",           icon: "📌" },
  { id: "form",      label: "신청 폼",          icon: "📝" },
  { id: "landing",   label: "소개 페이지",      icon: "🌐" },
] as const;

export function pageTypeIcon(type: string): string {
  return PAGE_TYPES.find(p => p.id === type)?.icon ?? "📄";
}
export function pageTypeLabel(type: string): string {
  return PAGE_TYPES.find(p => p.id === type)?.label ?? type;
}

// ── Types ─────────────────────────────────────────────────────────────────────

export type ProgramItem = {
  id: number;
  name: string;
  page_type: string;
  is_active: boolean;
  uuid: string | null;
  icon?: string | null;
};

export type Folder = {
  id: number;
  name: string;
  parent_id: number | null;
  sort_order: number;
  children: Folder[];
  program_count: number;
  programs: ProgramItem[];
};

// Drag payload — the item currently being dragged.
type DragItem = { kind: "program" | "folder"; id: number };

// ── Helpers ───────────────────────────────────────────────────────────────────

function flattenTree(folders: Folder[]): Folder[] {
  const result: Folder[] = [];
  const walk = (nodes: Folder[]) => {
    for (const n of nodes) { result.push(n); walk(n.children); }
  };
  walk(folders);
  return result;
}

function siblings(tree: Folder[], parentId: number | null): Folder[] {
  if (!parentId) return tree;
  return flattenTree(tree).find(f => f.id === parentId)?.children ?? [];
}

function findFolder(tree: Folder[], id: number): Folder | null {
  for (const n of tree) {
    if (n.id === id) return n;
    const f = findFolder(n.children, id);
    if (f) return f;
  }
  return null;
}

// Is `maybeChildId` inside the subtree rooted at `ancestorId`?
function isDescendant(tree: Folder[], ancestorId: number, maybeChildId: number): boolean {
  const anc = findFolder(tree, ancestorId);
  if (!anc) return false;
  const walk = (nodes: Folder[]): boolean =>
    nodes.some(n => n.id === maybeChildId || walk(n.children));
  return walk(anc.children);
}

// ── Inline rename ─────────────────────────────────────────────────────────────

function RenameInput({ initial, onCommit, onCancel }: {
  initial: string; onCommit: (v: string) => void; onCancel: () => void;
}) {
  const [v, setV] = useState(initial);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  return (
    <input
      ref={ref} value={v}
      onChange={e => setV(e.target.value)}
      onKeyDown={e => {
        if (e.key === "Enter") { e.stopPropagation(); onCommit(v.trim() || initial); }
        if (e.key === "Escape") { e.stopPropagation(); onCancel(); }
      }}
      onBlur={() => onCommit(v.trim() || initial)}
      onClick={e => e.stopPropagation()}
      style={{ flex: 1, fontSize: 12, padding: "1px 4px", margin: 0, border: "1px solid var(--p-400,#818cf8)", borderRadius: 3, outline: "none", background: "var(--bg-0)", color: "var(--text-1)", fontFamily: "inherit" }}
    />
  );
}

// ── Action button ─────────────────────────────────────────────────────────────

function Btn({ title, onClick, danger, children }: {
  title: string; onClick: () => void; danger?: boolean; children: React.ReactNode;
}) {
  return (
    <button title={title} onClick={e => { e.stopPropagation(); onClick(); }}
      style={{ background: "none", border: "none", cursor: "pointer", width: 20, height: 20, borderRadius: 3, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: danger ? "var(--danger,#ef4444)" : "var(--text-3)", padding: 0 }}
      onMouseEnter={e => (e.currentTarget.style.background = "var(--hover,#f3f4f6)")}
      onMouseLeave={e => (e.currentTarget.style.background = "none")}
    >{children}</button>
  );
}

// ── Program leaf row ──────────────────────────────────────────────────────────

function ProgramRow({ prog, depth, isActive, isAdmin, onSelect, onRename, onEdit, onDelete, onDragStart, onDragEnd }: {
  prog: ProgramItem; depth: number; isActive: boolean; isAdmin: boolean;
  onSelect: () => void; onRename: (name: string) => void; onEdit: () => void; onDelete: () => void;
  onDragStart: () => void; onDragEnd: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [ctx, setCtx] = useState<{ x: number; y: number } | null>(null);

  const commitRename = (name: string) => { setRenaming(false); if (name !== prog.name) onRename(name); };

  const ctxItems: CtxItem[] = isAdmin
    ? [
        { label: "열기", icon: "↗️", onClick: onSelect },
        { label: "이름 변경", icon: "✏️", onClick: () => setRenaming(true) },
        { label: "수정 (아이콘·주소·속성)", icon: "⚙️", onClick: onEdit },
        { sep: true },
        { label: "삭제", icon: "🗑️", onClick: onDelete, danger: true },
      ]
    : [{ label: "열기", icon: "↗️", onClick: onSelect }];

  return (
    <div>
      <div
        draggable={isAdmin && !renaming}
        onDragStart={e => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", `p${prog.id}`); onDragStart(); }}
        onDragEnd={onDragEnd}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onContextMenu={e => { e.preventDefault(); setCtx({ x: e.clientX, y: e.clientY }); }}
        style={{
          display: "flex", alignItems: "center", gap: 0,
          margin: "1px 4px 1px 0",
          paddingLeft: depth * 14 + 4,
          borderRadius: 6,
          background: isActive ? "rgba(99,102,241,0.12)" : hovered ? "rgba(99,102,241,0.05)" : "transparent",
          cursor: isAdmin && !renaming ? "grab" : "pointer", transition: "background 0.12s",
        }}
      >
        {/* indent spacer */}
        <span style={{ width: 11, flexShrink: 0, display: "inline-block" }} />

        <button
          onClick={onSelect}
          style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", padding: "5px 4px", textAlign: "left", minWidth: 0 }}
        >
          <span style={{ fontSize: 12, flexShrink: 0 }}>{prog.icon || pageTypeIcon(prog.page_type)}</span>
          {renaming ? (
            <RenameInput initial={prog.name} onCommit={commitRename} onCancel={() => setRenaming(false)} />
          ) : (
            <>
              <span style={{
                flex: 1, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                color: isActive ? "var(--p-600,#4f46e5)" : !prog.is_active ? "var(--text-3)" : "var(--text-2)",
                fontWeight: isActive ? 600 : 400,
                fontStyle: !prog.is_active ? "italic" : "normal",
              }}>
                {prog.name}
              </span>
              {!prog.is_active && (
                <span style={{ fontSize: 9, color: "#9CA3AF", flexShrink: 0, marginRight: 2 }}>비활성</span>
              )}
            </>
          )}
        </button>

        {/* Admin hover buttons — rename + edit */}
        {isAdmin && hovered && !renaming && (
          <div style={{ display: "flex", gap: 1, paddingRight: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
            <Btn title="이름 변경" onClick={() => setRenaming(true)}>✏</Btn>
            <Btn title="수정 (아이콘·주소·속성)" onClick={onEdit}>⚙</Btn>
          </div>
        )}
      </div>

      {ctx && <ContextMenu x={ctx.x} y={ctx.y} items={ctxItems} onClose={() => setCtx(null)} />}
    </div>
  );
}

// ── Folder row ────────────────────────────────────────────────────────────────

function FolderRow({
  folder, depth, isActive, isAdmin, activeIdent, isDropTarget,
  onSelect, onRename, onDelete, onAddChild, onMoveUp, onMoveDown,
  onAddProgram,
  onProgramSelect, onProgramRename, onProgramEdit, onProgramDelete,
  onDragStartFolder, onDragStartProgram, onDragEnd,
  onDragOverFolder, onDropOnFolder,
  children,
}: {
  folder: Folder; depth: number; isActive: boolean; isAdmin: boolean; activeIdent: string | null; isDropTarget: boolean;
  onSelect: () => void; onRename: (name: string) => void; onDelete: () => void;
  onAddChild: () => void; onMoveUp: () => void; onMoveDown: () => void;
  canMoveUp: boolean; canMoveDown: boolean;
  onAddProgram: () => void;
  onProgramSelect: (prog: ProgramItem) => void;
  onProgramRename: (prog: ProgramItem, name: string) => void;
  onProgramEdit: (prog: ProgramItem) => void;
  onProgramDelete: (p: ProgramItem) => void;
  onDragStartFolder: (id: number) => void;
  onDragStartProgram: (id: number) => void;
  onDragEnd: () => void;
  onDragOverFolder: (id: number) => void;
  onDropOnFolder: (id: number) => void;
  children?: React.ReactNode;
}) {
  // All folders expanded by default (consistent across every depth); users can
  // still collapse individual folders with the chevron.
  const [open,     setOpen]     = useState(true);
  const [renaming, setRenaming] = useState(false);
  const [hovered,  setHovered]  = useState(false);
  const [ctx,      setCtx]      = useState<{ x: number; y: number } | null>(null);

  const commitRename = (name: string) => {
    setRenaming(false);
    if (name !== folder.name) onRename(name);
  };

  const ctxItems: CtxItem[] = isAdmin ? [
    { label: "새 프로그램",    icon: "📄", onClick: onAddProgram },
    { label: "하위 폴더 추가", icon: "📁", onClick: onAddChild },
    { sep: true },
    { label: "이름 변경",      icon: "✏️", onClick: () => setRenaming(true) },
    { label: "위로 이동",      icon: "↑",  onClick: onMoveUp },
    { label: "아래로 이동",    icon: "↓",  onClick: onMoveDown },
    { sep: true },
    { label: "삭제",           icon: "🗑️", onClick: onDelete, danger: true },
  ] : [];

  const hasChildren = folder.children.length > 0;
  const hasPrograms = folder.programs.length > 0;

  return (
    <div>
      <div
        draggable={isAdmin && !renaming}
        onDragStart={e => { e.stopPropagation(); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", `f${folder.id}`); onDragStartFolder(folder.id); }}
        onDragEnd={onDragEnd}
        onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; onDragOverFolder(folder.id); }}
        onDrop={e => { e.preventDefault(); e.stopPropagation(); onDropOnFolder(folder.id); }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onContextMenu={e => { e.preventDefault(); if (isAdmin) setCtx({ x: e.clientX, y: e.clientY }); }}
        style={{
          display: "flex", alignItems: "center",
          margin: "1px 4px 1px 0", paddingLeft: depth * 14 + 4,
          borderRadius: 6,
          background: isDropTarget ? "rgba(99,102,241,0.2)" : isActive ? "rgba(99,102,241,0.12)" : hovered ? "rgba(99,102,241,0.05)" : "transparent",
          outline: isDropTarget ? "1.5px dashed var(--p-500,#6366f1)" : "none",
          cursor: isAdmin && !renaming ? "grab" : "pointer", transition: "background 0.12s",
        }}
      >
        {/* Expand toggle */}
        <button
          onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
          style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 2px", color: "var(--text-3)", flexShrink: 0, display: "flex", alignItems: "center" }}
        >
          {(hasChildren || hasPrograms)
            ? (open ? <I.ChevD size={11}/> : <I.ChevR size={11}/>)
            : <span style={{ width: 11, display: "inline-block" }} />}
        </button>

        {/* Folder name */}
        <button
          onClick={onSelect}
          style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", padding: "5px 4px", textAlign: "left", minWidth: 0 }}
        >
          <span style={{ fontSize: 13, flexShrink: 0 }}>📁</span>
          {renaming ? (
            <RenameInput initial={folder.name} onCommit={commitRename} onCancel={() => setRenaming(false)} />
          ) : (
            <>
              <span style={{ flex: 1, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: isActive ? "var(--p-600,#4f46e5)" : "var(--text-2)", fontWeight: isActive ? 600 : 400 }}>
                {folder.name}
              </span>
              {folder.program_count > 0 && !open && (
                <span style={{ fontSize: 10, color: "var(--text-3)", flexShrink: 0, marginRight: 2 }}>{folder.program_count}</span>
              )}
            </>
          )}
        </button>

        {/* Admin hover buttons */}
        {isAdmin && hovered && !renaming && (
          <div style={{ display: "flex", gap: 1, paddingRight: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
            <Btn title="새 프로그램" onClick={onAddProgram}>📄</Btn>
            <Btn title="하위 폴더 추가" onClick={onAddChild}>+</Btn>
            <Btn title="이름 변경" onClick={() => setRenaming(true)}>✏</Btn>
          </div>
        )}
      </div>

      {/* Expanded children */}
      {open && (
        <>
          {/* Sub-folder tree */}
          {hasChildren && children}

          {/* Programs in this folder */}
          {folder.programs.map(prog => (
            <ProgramRow
              key={prog.id}
              prog={prog}
              depth={depth + 1}
              isActive={activeIdent != null && (activeIdent === prog.uuid || activeIdent === String(prog.id))}
              isAdmin={isAdmin}
              onSelect={() => onProgramSelect(prog)}
              onRename={name => onProgramRename(prog, name)}
              onEdit={() => onProgramEdit(prog)}
              onDelete={() => onProgramDelete(prog)}
              onDragStart={() => onDragStartProgram(prog.id)}
              onDragEnd={onDragEnd}
            />
          ))}
        </>
      )}

      {ctx && <ContextMenu x={ctx.x} y={ctx.y} items={ctxItems} onClose={() => setCtx(null)} />}
    </div>
  );
}

// ── New folder inline input ───────────────────────────────────────────────────

function NewFolderInput({ parentId, sortOrder, onCreated, onCancel }: {
  parentId: number | null; sortOrder: number;
  onCreated: (folder: Folder) => void; onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);

  const submit = async () => {
    const n = name.trim();
    if (!n) { onCancel(); return; }
    setSaving(true);
    try {
      const res = await api.post<Folder>("/programs/folders", { name: n, parent_id: parentId, sort_order: sortOrder });
      onCreated(res.data);
    } catch (e: any) {
      toast.error("폴더 추가 실패", e?.response?.data?.error?.message ?? "");
    } finally { setSaving(false); }
  };

  return (
    <div style={{ display: "flex", gap: 4, margin: "2px 6px", padding: "4px 8px", alignItems: "center", background: "rgba(99,102,241,0.07)", borderRadius: 6, border: "1px dashed var(--p-400,#818cf8)" }}>
      <span style={{ fontSize: 13 }}>📁</span>
      <input ref={ref} value={name} onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === "Enter") submit(); if (e.key === "Escape") onCancel(); }} placeholder="폴더 이름..." disabled={saving}
        style={{ flex: 1, fontSize: 12, border: "none", outline: "none", background: "transparent", color: "var(--text-1)", fontFamily: "inherit" }} />
      <Btn title="추가" onClick={submit}>✓</Btn>
      <Btn title="취소" onClick={onCancel}>✕</Btn>
    </div>
  );
}

// ── Quick-add program modal ────────────────────────────────────────────────────

function QuickAddProgram({ folderId, folderName, onClose, onCreated }: {
  folderId: number; folderName: string;
  onClose: () => void; onCreated: (prog: { id: number; uuid: string }) => void;
}) {
  const [name, setName] = useState("");
  const [uuid, setUuid] = useState("");
  const [icon, setIcon] = useState(DEFAULT_ICON);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const res = await api.post<{ id: number; uuid: string }>("/programs", {
        name: name.trim(), page_type: "custom", icon, folder_id: folderId, category: "기타",
        uuid: uuid.trim() || undefined,
      });
      onCreated(res.data);
    } catch (e: any) {
      toast.error("추가 실패", e?.response?.data?.error?.message ?? "");
    } finally { setSaving(false); }
  };

  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={e => e.stopPropagation()} style={{ width: 400, maxWidth: "95vw" }}>
        <div style={{ padding: "14px 16px 10px", borderBottom: "1px solid var(--border)" }}>
          <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 4 }}>📁 {folderName}</div>
          <h3 style={{ margin: 0, fontSize: 14 }}>새 프로그램</h3>
        </div>
        <div style={{ padding: 16, display: "grid", gap: 12 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", display: "block", marginBottom: 4 }}>이름 *</label>
            <input ref={inputRef} className="t-input" value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === "Enter" && submit()} placeholder="프로그램 이름" />
          </div>

          <UuidField uuid={uuid} setUuid={setUuid} />

          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              아이콘 <span style={{ fontSize: 18 }}>{icon}</span>
            </label>
            <IconPicker value={icon} onChange={setIcon} height={180} />
          </div>
        </div>
        <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="t-btn" onClick={onClose} disabled={saving}>취소</button>
          <button className="t-btn t-btn-primary" onClick={submit} disabled={saving || !name.trim()}>{saving ? "추가 중…" : "추가"}</button>
        </div>
      </div>
    </div>
  );
}

// Shared UUID / slug input — used as both the link address and the local folder name.
export function UuidField({ uuid, setUuid }: { uuid: string; setUuid: (v: string) => void }) {
  const clean = (v: string) => v.replace(/[^A-Za-z0-9-]/g, "");
  return (
    <div>
      <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-3)", display: "block", marginBottom: 4 }}>
        UUID / 링크 주소 <span style={{ fontWeight: 400 }}>(선택 — 비우면 자동 생성)</span>
      </label>
      <input
        className="t-input"
        value={uuid}
        onChange={e => setUuid(clean(e.target.value))}
        placeholder="예: my-service-2026"
        style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}
      />
      <div style={{ fontSize: 10, color: "var(--text-3)", marginTop: 5, lineHeight: 1.6 }}>
        🔗 링크: <code>/programs/{uuid || "<자동 UUID>"}</code><br />
        📂 폴더: <code>data/programs/{uuid || "<자동 UUID>"}</code>
      </div>
    </div>
  );
}

// ── Edit program modal (아이콘 · 주소 · 속성 통합 수정) ─────────────────────────

const CATEGORIES = ["기획", "계획", "디자인", "영상", "기타"];
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: "var(--text-3)", display: "block", marginBottom: 4 };

function EditProgram({ programId, onClose, onSaved }: {
  programId: number; onClose: () => void; onSaved: (newUuid: string | null) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [name, setName]       = useState("");
  const [icon, setIcon]       = useState(DEFAULT_ICON);
  const [uuid, setUuid]       = useState("");
  const [pageType, setPageType] = useState("custom");
  const [category, setCategory] = useState("기타");
  const [isActive, setIsActive] = useState(true);
  const [desc, setDesc]       = useState("");
  const origUuid = useRef("");

  useEffect(() => {
    api.get<any>(`/programs/${programId}`).then(r => {
      const p = r.data;
      setName(p.name ?? "");
      setIcon(p.icon || DEFAULT_ICON);
      setUuid(p.uuid ?? "");
      origUuid.current = p.uuid ?? "";
      setPageType(p.page_type ?? "custom");
      setCategory(p.category ?? "기타");
      setIsActive(!!p.is_active);
      setDesc(p.description ?? "");
    }).catch(() => toast.error("프로그램 정보를 불러오지 못했습니다"))
      .finally(() => setLoading(false));
  }, [programId]);

  const uuidChanged = uuid.trim() !== origUuid.current;

  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const body: any = {
        name: name.trim(), icon, page_type: pageType, category,
        is_active: isActive, description: desc.trim() || null,
      };
      if (uuidChanged) body.uuid = uuid.trim();   // 변경 시 서버 폴더도 rename
      await api.patch(`/programs/${programId}`, body);
      toast.success("수정되었습니다");
      onSaved(uuidChanged ? uuid.trim() : null);
    } catch (e: any) {
      toast.error("수정 실패", e?.response?.data?.error?.message ?? "");
    } finally { setSaving(false); }
  };

  return (
    <div className="t-overlay" onClick={onClose}>
      <div className="t-cmdk" onClick={e => e.stopPropagation()} style={{ width: 440, maxWidth: "95vw", maxHeight: "90vh", overflow: "auto", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "14px 16px 10px", borderBottom: "1px solid var(--border)", flexShrink: 0 }}>
          <h3 style={{ margin: 0, fontSize: 14 }}>⚙️ 프로그램 수정</h3>
        </div>
        {loading ? (
          <div style={{ padding: 28, textAlign: "center", color: "var(--text-3)", fontSize: 13 }}>불러오는 중…</div>
        ) : (
          <div style={{ padding: 16, display: "grid", gap: 12 }}>
            <div>
              <label style={lbl}>이름 *</label>
              <input className="t-input" value={name} onChange={e => setName(e.target.value)} placeholder="프로그램 이름" />
            </div>

            <UuidField uuid={uuid} setUuid={setUuid} />
            {uuidChanged && origUuid.current && (
              <div style={{ fontSize: 11, color: "#b45309", background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.28)", borderRadius: 6, padding: "7px 10px", lineHeight: 1.55 }}>
                ⚠️ 주소를 바꾸면 서버 폴더 <code>data/programs/{origUuid.current}</code> → <code>data/programs/{uuid.trim() || "?"}</code> 로 이름이 변경되고, 기존 <code>/programs/{origUuid.current}</code> 링크는 더 이상 열리지 않습니다.
              </div>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>유형</label>
                <select className="t-input" value={pageType} onChange={e => setPageType(e.target.value)}>
                  {PAGE_TYPES.map(t => <option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>분류</label>
                <select className="t-input" value={category} onChange={e => setCategory(e.target.value)}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-2)", cursor: "pointer" }}>
              <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
              활성 <span style={{ fontSize: 11, color: "var(--text-3)" }}>(해제 시 일반 사용자에게 숨김)</span>
            </label>

            <div>
              <label style={lbl}>설명</label>
              <textarea className="t-input" value={desc} onChange={e => setDesc(e.target.value)} rows={2} placeholder="(선택)" style={{ resize: "vertical", fontFamily: "inherit" }} />
            </div>

            <div>
              <label style={{ ...lbl, display: "flex", alignItems: "center", gap: 6 }}>아이콘 <span style={{ fontSize: 18 }}>{icon}</span></label>
              <IconPicker value={icon} onChange={setIcon} height={170} />
            </div>
          </div>
        )}
        <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border)", display: "flex", gap: 8, justifyContent: "flex-end", flexShrink: 0 }}>
          <button className="t-btn" onClick={onClose} disabled={saving}>취소</button>
          <button className="t-btn t-btn-primary" onClick={submit} disabled={saving || loading || !name.trim()}>{saving ? "저장 중…" : "저장"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Main sidebar ──────────────────────────────────────────────────────────────

export function ProgramsSidebar({ onCollapse }: { onCollapse: () => void }) {
  const nav = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Active states
  const activeFolderId  = searchParams.get("folder") ? Number(searchParams.get("folder")) : null;
  const activeFilter    = searchParams.get("filter") ?? "";
  const pathMatch       = location.pathname.match(/^\/programs\/([^/?]+)$/);
  const activeIdent     = pathMatch ? decodeURIComponent(pathMatch[1]) : null;

  const isAdmin = useAuth(s => s.hasRole?.("SUPER_ADMIN") || s.hasRole?.("ADMIN"));

  const [tree,    setTree]    = useState<Folder[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingAt, setAddingAt] = useState<{ parentId: number | null } | null>(null);
  const [addingProgramAt, setAddingProgramAt] = useState<{ folderId: number; folderName: string } | null>(null);
  const [editingProgramId, setEditingProgramId] = useState<number | null>(null);

  // Drag-and-drop state
  const dragItem = useRef<DragItem | null>(null);
  const [dropTarget, setDropTarget] = useState<number | "root" | null>(null);

  const reload = () =>
    api.get<Folder[]>("/programs/folders")
      .then(r => setTree(r.data ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));

  useEffect(() => { reload(); }, []);

  const rename = async (id: number, name: string) => {
    try {
      const res = await api.patch<Folder>(`/programs/folders/${id}`, { name });
      setTree(prev => applyUpdate(prev, id, f => ({ ...f, name: res.data.name })));
    } catch { toast.error("이름 변경 실패"); }
  };

  const renameProgram = async (prog: ProgramItem, name: string) => {
    const n = name.trim();
    if (!n || n === prog.name) return;
    try {
      await api.patch(`/programs/${prog.id}`, { name: n });
      reload();
    } catch { toast.error("이름 변경 실패"); }
  };

  const deleteFolder = async (id: number, name: string) => {
    if (!confirm(`"${name}" 폴더를 삭제하시겠습니까?\n하위 폴더와 프로그램 연결이 해제됩니다.`)) return;
    try {
      await api.delete(`/programs/folders/${id}`);
      toast.success("폴더 삭제됨");
      reload();
    } catch { toast.error("삭제 실패"); }
  };

  const deleteProgram = async (prog: ProgramItem) => {
    if (!confirm(`"${prog.name}" 프로그램을 삭제하시겠습니까?`)) return;
    try {
      await api.delete(`/programs/${prog.id}`);
      toast.success("프로그램 삭제됨");
      reload();
      // If we were on that program's page, go back to programs index
      if (activeIdent != null && (activeIdent === prog.uuid || activeIdent === String(prog.id))) nav("/programs");
    } catch { toast.error("삭제 실패"); }
  };

  const moveFolder = async (id: number, parentId: number | null, dir: "up" | "down") => {
    const all = siblings(tree, parentId);
    const idx = all.findIndex(f => f.id === id);
    const newOrder = [...all];
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= newOrder.length) return;
    [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];
    try {
      const res = await api.put<Folder[]>("/programs/folders/reorder", { ids: newOrder.map(f => f.id) });
      setTree(res.data);
    } catch { toast.error("순서 변경 실패"); }
  };

  // ── Drag-and-drop move ──────────────────────────────────────────────────────
  const endDrag = () => { dragItem.current = null; setDropTarget(null); };

  const handleDropOn = async (targetFolderId: number | null) => {
    const item = dragItem.current;
    endDrag();
    if (!item) return;

    if (item.kind === "program") {
      try {
        await api.patch(`/programs/${item.id}`, { folder_id: targetFolderId });
        toast.success("이동됨");
        reload();
      } catch { toast.error("이동 실패"); }
      return;
    }

    // folder → folder (or root)
    if (targetFolderId === item.id) return;                       // onto itself
    if (targetFolderId != null && isDescendant(tree, item.id, targetFolderId)) {
      toast.error("자기 하위 폴더로는 이동할 수 없습니다");
      return;
    }
    try {
      await api.patch(`/programs/folders/${item.id}`, { parent_id: targetFolderId });
      toast.success("이동됨");
      reload();
    } catch { toast.error("이동 실패"); }
  };

  const goFolder  = (id: number | null) => id == null ? nav("/programs") : nav(`/programs?folder=${id}`);
  const goProgram = (prog: ProgramItem) => nav(`/programs/${encodeURIComponent(prog.uuid ?? String(prog.id))}`);
  const goBookings = () => nav("/programs?filter=bookings");

  const renderFolders = (folders: Folder[], depth: number) =>
    folders.map((f, idx) => (
      <div key={f.id}>
        <FolderRow
          folder={f} depth={depth}
          isActive={activeFolderId === f.id}
          activeIdent={activeIdent}
          isAdmin={!!isAdmin}
          isDropTarget={dropTarget === f.id}
          onSelect={() => goFolder(f.id)}
          onRename={name => rename(f.id, name)}
          onDelete={() => deleteFolder(f.id, f.name)}
          onAddChild={() => setAddingAt({ parentId: f.id })}
          onAddProgram={() => setAddingProgramAt({ folderId: f.id, folderName: f.name })}
          onMoveUp={() => moveFolder(f.id, f.parent_id, "up")}
          onMoveDown={() => moveFolder(f.id, f.parent_id, "down")}
          canMoveUp={idx > 0}
          canMoveDown={idx < folders.length - 1}
          onProgramSelect={goProgram}
          onProgramRename={renameProgram}
          onProgramEdit={prog => setEditingProgramId(prog.id)}
          onProgramDelete={deleteProgram}
          onDragStartFolder={id => { dragItem.current = { kind: "folder", id }; }}
          onDragStartProgram={id => { dragItem.current = { kind: "program", id }; }}
          onDragEnd={endDrag}
          onDragOverFolder={id => { if (dragItem.current) setDropTarget(id); }}
          onDropOnFolder={id => handleDropOn(id)}
        >
          {f.children.length > 0 && renderFolders(f.children, depth + 1)}
        </FolderRow>

        {addingAt?.parentId === f.id && (
          <div style={{ paddingLeft: (depth + 1) * 14 + 8 }}>
            <NewFolderInput
              parentId={f.id} sortOrder={f.children.length}
              onCreated={() => { setAddingAt(null); reload(); }}
              onCancel={() => setAddingAt(null)}
            />
          </div>
        )}
      </div>
    ));

  // Active indicator logic: highlight "전체 서비스" only when on /programs with no folder/filter/program
  const allActive = !activeFolderId && !activeFilter && !activeIdent;
  const rootDrop = dropTarget === "root";

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "14px 12px 10px", borderBottom: "1px solid var(--border)", flexShrink: 0, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "var(--text-1)" }}>프로그램</h3>
          <p style={{ margin: "2px 0 0", fontSize: 11, color: "var(--text-3)" }}>서비스 카탈로그</p>
        </div>
        <button onClick={onCollapse} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", padding: 2, borderRadius: 4 }} title="접기">
          <I.PanelL size={14}/>
        </button>
      </div>

      {/* "전체 서비스" link — also a drop target for moving to the root */}
      <div style={{ padding: "6px 8px 2px", flexShrink: 0 }}>
        <button
          onClick={() => goFolder(null)}
          onDragOver={e => { if (dragItem.current) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDropTarget("root"); } }}
          onDrop={e => { e.preventDefault(); handleDropOn(null); }}
          style={{ display: "flex", alignItems: "center", gap: 7, width: "100%", border: "none", cursor: "pointer", textAlign: "left", padding: "6px 10px", borderRadius: 6, fontSize: 13, background: rootDrop ? "rgba(99,102,241,0.2)" : allActive ? "rgba(99,102,241,0.12)" : "transparent", outline: rootDrop ? "1.5px dashed var(--p-500,#6366f1)" : "none", color: allActive ? "var(--p-600,#4f46e5)" : "var(--text-2)", fontWeight: allActive ? 600 : 400 }}
          onMouseEnter={e => { if (!allActive && !rootDrop) (e.currentTarget as HTMLElement).style.background = "rgba(99,102,241,0.05)"; }}
          onMouseLeave={e => { if (!allActive && !rootDrop) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
        >
          <span>🗂️</span><span>전체 서비스</span>
        </button>
      </div>

      {/* Folder tree */}
      <div style={{ flex: 1, overflowY: "auto", padding: "2px 4px" }}>
        {loading && <div style={{ padding: "12px 16px", color: "var(--text-3)", fontSize: 12 }}>불러오는 중…</div>}

        {!loading && (
          <div style={{ display: "flex", alignItems: "center", padding: "6px 8px 2px 10px" }}>
            <span style={{ flex: 1, fontSize: 10, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>폴더</span>
            {isAdmin && (
              <button title="루트 폴더 추가" onClick={() => setAddingAt({ parentId: null })}
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", padding: "2px 4px", borderRadius: 3, fontSize: 14, lineHeight: 1, display: "flex", alignItems: "center" }}
                onMouseOver={e => (e.currentTarget.style.color = "var(--p-500,#6366f1)")}
                onMouseOut={e => (e.currentTarget.style.color = "var(--text-3)")}
              >
                <I.Plus size={12}/>
              </button>
            )}
          </div>
        )}

        {renderFolders(tree, 0)}

        {addingAt?.parentId === null && (
          <NewFolderInput
            parentId={null} sortOrder={tree.length}
            onCreated={() => { setAddingAt(null); reload(); }}
            onCancel={() => setAddingAt(null)}
          />
        )}

        {tree.length === 0 && !loading && (
          <div style={{ padding: "16px 12px", color: "var(--text-3)", fontSize: 12, textAlign: "center" }}>
            {isAdmin ? "위 + 버튼으로 폴더를 추가하세요" : "폴더가 없습니다"}
          </div>
        )}
      </div>

      {/* My bookings */}
      <div style={{ borderTop: "1px solid var(--border)", padding: "6px 8px", flexShrink: 0 }}>
        <button
          onClick={goBookings}
          style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", border: "none", cursor: "pointer", textAlign: "left", padding: "8px 10px", borderRadius: 6, fontSize: 13, background: activeFilter === "bookings" ? "rgba(99,102,241,0.12)" : "transparent", color: activeFilter === "bookings" ? "var(--p-600)" : "var(--text-2)", fontWeight: activeFilter === "bookings" ? 600 : 400, transition: "background 0.12s" }}
          onMouseEnter={e => { if (activeFilter !== "bookings") (e.currentTarget as HTMLElement).style.background = "rgba(99,102,241,0.05)"; }}
          onMouseLeave={e => { if (activeFilter !== "bookings") (e.currentTarget as HTMLElement).style.background = "transparent"; }}
        >
          <span>📋</span><span>내 예약 내역</span>
        </button>
      </div>

      {/* Quick-add program modal */}
      {addingProgramAt && (
        <QuickAddProgram
          folderId={addingProgramAt.folderId}
          folderName={addingProgramAt.folderName}
          onClose={() => setAddingProgramAt(null)}
          onCreated={prog => {
            setAddingProgramAt(null);
            reload();
            nav(`/programs/${encodeURIComponent(prog.uuid ?? String(prog.id))}`);
          }}
        />
      )}

      {/* Edit program modal (아이콘·주소·속성) */}
      {editingProgramId != null && (
        <EditProgram
          programId={editingProgramId}
          onClose={() => setEditingProgramId(null)}
          onSaved={newUuid => {
            // 현재 보던 프로그램의 주소가 바뀌었으면 새 주소로 따라 이동
            const edited = flattenTree(tree).flatMap(f => f.programs).find(p => p.id === editingProgramId);
            const oldIdent = edited?.uuid ?? String(editingProgramId);
            setEditingProgramId(null);
            reload();
            if (newUuid && activeIdent != null && activeIdent === oldIdent) {
              nav(`/programs/${encodeURIComponent(newUuid)}`);
            }
          }}
        />
      )}
    </div>
  );
}

// ── Tree helpers ──────────────────────────────────────────────────────────────

function applyUpdate(tree: Folder[], id: number, fn: (f: Folder) => Folder): Folder[] {
  return tree.map(f => {
    if (f.id === id) return fn(f);
    return { ...f, children: applyUpdate(f.children, id, fn) };
  });
}
